import { useState, useEffect } from 'react';
import { getProspectJourney, tickMilestone, addRevision, completeExecution } from '../../../api/prospectApi';
import StageMeetingLog from '../StageMeetingLog/StageMeetingLog';
import DocChecklistBuilder from '../DocChecklistBuilder/DocChecklistBuilder';
import DocCollectionWorkspace from '../DocCollectionWorkspace/DocCollectionWorkspace';
import MilestoneFilePanel from '../MilestoneFilePanel/MilestoneFilePanel';
import './ProspectWorkspace.css';

const STAGE_KEYS_ORDERED = [
  'PRELIMINARY_REVIEW',
  'INITIAL_REVIEW_DISCUSSION',
  'DOCUMENT_COLLECTION',
  'FINAL_WEALTH_STRATEGY',
  'FINAL_STRATEGY_DISCUSSION',
  'EXECUTION',
];

const STAGE_COLORS = {
  PRELIMINARY_REVIEW:        '#5a7a85',
  INITIAL_REVIEW_DISCUSSION: '#004358',
  DOCUMENT_COLLECTION:       '#d97706',
  FINAL_WEALTH_STRATEGY:     '#7c3aed',
  FINAL_STRATEGY_DISCUSSION: '#4338ca',
  EXECUTION:                 '#16a34a',
};

const STAGE_NUMBERS = {
  PRELIMINARY_REVIEW: 1,
  INITIAL_REVIEW_DISCUSSION: 2,
  DOCUMENT_COLLECTION: 3,
  FINAL_WEALTH_STRATEGY: 4,
  FINAL_STRATEGY_DISCUSSION: 5,
  EXECUTION: 6,
};

// Milestones that get file management panel
const FILE_MILESTONES = new Set([
  'PROFILE_DATA_REVIEWED',
  'STRATEGY_PREPARED',
  'PROCESSED_DATA_REVIEWED',
  'FINAL_STRATEGY_PREPARED',
]);

// Milestones that also show "Download Profile Snapshot"
const PROFILE_SNAPSHOT_MILESTONES = new Set(['PROFILE_DATA_REVIEWED']);

// Stages that have inner sub-tabs
const STAGE_TABS = {
  INITIAL_REVIEW_DISCUSSION: [
    { key: 'checklist',     label: '☑ Checklist' },
    { key: 'meetings',      label: '📅 Meetings' },
    { key: 'doc_checklist', label: '📋 Doc Checklist' },
  ],
  FINAL_STRATEGY_DISCUSSION: [
    { key: 'checklist', label: '☑ Checklist' },
    { key: 'meetings',  label: '📅 Meetings' },
  ],
};

function formatDate(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function daysSince(isoStr) {
  if (!isoStr) return 0;
  return Math.floor((Date.now() - new Date(isoStr).getTime()) / 86400000);
}

function totalOnboardingDays(stages) {
  if (stages[0] && stages[0].started_at) {
    return daysSince(stages[0].started_at);
  }
  return 0;
}

export default function ProspectWorkspace({ prospect }) {
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedStageKey, setSelectedStageKey] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [completingExecution, setCompletingExecution] = useState(false);
  const [selectedTab, setSelectedTab] = useState('checklist');

  useEffect(() => {
    if (!prospect) {
      setJourney(null);
      setSelectedStageKey(null);
      return;
    }
    loadJourney();
  }, [prospect]);

  // When journey loads, default selection to the active stage
  useEffect(() => {
    if (!journey) return;
    const activeStage = journey.stages.find(s => s.status === 'IN_PROGRESS');
    if (activeStage) {
      setSelectedStageKey(activeStage.stage_key);
    }
  }, [journey]);

  // Reset tab to checklist whenever the selected stage changes
  useEffect(() => {
    setSelectedTab('checklist');
  }, [selectedStageKey]);

  async function loadJourney() {
    setLoading(true);
    setError('');
    try {
      const data = await getProspectJourney(prospect.id);
      setJourney(data);
    } catch (ex) {
      setError(ex.message || 'Failed to load journey');
    } finally {
      setLoading(false);
    }
  }

  function handleStageCircleClick(stageKey, status) {
    if (status === 'LOCKED') return;
    setSelectedStageKey(stageKey);
    setExpandedNotes({});
  }

  async function handleTickMilestone(stageKey, milestoneKey, isChecked) {
    try {
      const updated = await tickMilestone(prospect.id, stageKey, milestoneKey, isChecked, null);
      setJourney(updated);
    } catch (ex) {
      alert('Failed to update milestone: ' + ex.message);
    }
  }

  async function handleSaveMilestoneNotes(stageKey, milestoneKey, isChecked, notes) {
    try {
      const updated = await tickMilestone(prospect.id, stageKey, milestoneKey, isChecked, notes);
      setJourney(updated);
    } catch (ex) {
      console.error('Failed to save milestone notes:', ex);
    }
  }

  async function handleAddRevision(stageKey, milestoneKey) {
    const notes = window.prompt('Revision notes:');
    if (!notes || !notes.trim()) return;
    try {
      const updated = await addRevision(prospect.id, stageKey, milestoneKey, notes.trim());
      setJourney(updated);
    } catch (ex) {
      alert('Failed to add revision: ' + ex.message);
    }
  }

async function handleCompleteExecution() {
    if (!window.confirm('Mark execution as complete? This will graduate this prospect to Active Client status.')) return;
    setCompletingExecution(true);
    try {
      await completeExecution(prospect.id);
      loadJourney();
    } catch (ex) {
      alert('Cannot complete execution: ' + ex.message);
    } finally {
      setCompletingExecution(false);
    }
  }

  function hasDot(tabKey, stage) {
    if (tabKey === 'meetings') return (stage.meetings || []).length > 0;
    if (tabKey === 'doc_checklist') return (stage.proposed_doc_items || []).some(d => d.selected);
    return false;
  }

  function toggleNotes(noteKey) {
    setExpandedNotes(prev => ({ ...prev, [noteKey]: !prev[noteKey] }));
  }

  if (!prospect) {
    return (
      <div className="pw__empty">
        <div className="pw__empty-icon">👈</div>
        <div className="pw__empty-text">Select a prospect to view their journey</div>
      </div>
    );
  }

  if (loading) return <div className="pw__loading">Loading journey...</div>;
  if (error)   return <div className="pw__error">{error}</div>;
  if (!journey) return null;

  const stages = journey.stages || [];
  const totalDays = totalOnboardingDays(stages);
  const selectedStage = stages.find(s => s.stage_key === selectedStageKey);
  const isReadOnly = selectedStage && selectedStage.status === 'COMPLETE';

  return (
    <div className="pw__panel">
      {/* Compact topbar — identity + stage navigator in one row */}
      <div className="pw__topbar">
        <div className="pw__topbar-identity">
          <span className="pw__name">{prospect.name}</span>
          {prospect.phone && <span className="pw__phone">{prospect.phone}</span>}
          <span className="pw__onboarding-days">Day {totalDays}</span>
        </div>

        <div className="pw__topbar-progress">
          {STAGE_KEYS_ORDERED.map((key, idx) => {
            const stage = stages.find(s => s.stage_key === key);
            const status = stage ? stage.status : 'LOCKED';
            const isSelected = selectedStageKey === key;
            const isClickable = status !== 'LOCKED';
            const color = STAGE_COLORS[key];
            let circleClass = 'pw__progress-circle';
            let circleContent = idx + 1;

            if (status === 'COMPLETE') {
              circleClass += ' pw__progress-circle--complete';
              circleContent = '✓';
            } else if (status === 'IN_PROGRESS') {
              circleClass += ' pw__progress-circle--active';
              circleContent = idx + 1;
            } else {
              circleClass += ' pw__progress-circle--locked';
            }

            if (isSelected) circleClass += ' pw__progress-circle--selected';
            if (isClickable) circleClass += ' pw__progress-circle--clickable';

            return (
              <div key={key} className="pw__progress-step">
                <div
                  className={circleClass}
                  style={status === 'IN_PROGRESS' ? { background: color, borderColor: color } : {}}
                  onClick={() => handleStageCircleClick(key, status)}
                  title={status === 'LOCKED' ? 'Stage locked' : (stage ? stage.label : key)}
                >
                  {circleContent}
                </div>
                {idx < STAGE_KEYS_ORDERED.length - 1 && (
                  <div className={`pw__progress-line ${status === 'COMPLETE' ? 'pw__progress-line--done' : ''}`} />
                )}
              </div>
            );
          })}
          {selectedStage && (
            <div className="pw__progress-stage-label">
              <span style={{ color: STAGE_COLORS[selectedStage.stage_key] }}>
                S{STAGE_NUMBERS[selectedStage.stage_key]} · {selectedStage.label}
              </span>
              {isReadOnly && <span className="pw__read-only-badge">✓ {formatDate(selectedStage.completed_at)}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Stage workspace */}
      <div className="pw__workspace">
        {!selectedStage ? (
          <div className="pw__all-done">All stages complete. Prospect graduated to Active Client.</div>
        ) : selectedStage.status === 'LOCKED' ? (
          <div className="pw__locked-state">
            <span className="pw__locked-icon">🔒</span>
            <span>This stage is locked until the previous stage is completed.</span>
          </div>
        ) : (
          renderStageWorkspace(selectedStage, isReadOnly)
        )}
      </div>

    </div>
  );

  function renderStageWorkspace(stage, readOnly) {
    const tabs        = STAGE_TABS[stage.stage_key];
    const hasDocTable = stage.stage_key === 'DOCUMENT_COLLECTION';
    const activeTab   = tabs ? selectedTab : null;
    const allRequiredDone = (stage.milestones || []).every(m => !m.required || m.checked);

    return (
      <div className="pw__stage-workspace">

        {/* Tab bar — only for stages that have multiple sections */}
        {tabs && (
          <div className="pw__tab-bar">
            {tabs.map(function(tab) {
              return (
                <button
                  key={tab.key}
                  className={'pw__tab' + (selectedTab === tab.key ? ' pw__tab--active' : '')}
                  onClick={() => setSelectedTab(tab.key)}
                >
                  {tab.label}
                  {hasDot(tab.key, stage) && <span className="pw__tab-dot" />}
                </button>
              );
            })}
          </div>
        )}

        {/* Checklist — always shown for non-doc-table stages; tab-gated for tabbed stages */}
        {!hasDocTable && (!tabs || activeTab === 'checklist') && (
          <div className="pw__checklist-section">
            <div className="pw__milestones">
              {(stage.milestones || []).map(m => renderMilestone(stage, m, readOnly))}
            </div>
            {!readOnly && allRequiredDone && stage.stage_key === 'EXECUTION' && (
              <div className="pw__complete-execution">
                <button
                  className="btn--success"
                  onClick={handleCompleteExecution}
                  disabled={completingExecution}
                >
                  {completingExecution ? 'Completing...' : '🎉 Complete Execution — Graduate to Active Client'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Meetings — tab-gated */}
        {activeTab === 'meetings' && (
          <StageMeetingLog
            prospectId={prospect.id}
            stageKey={stage.stage_key}
            meetings={stage.meetings || []}
            onJourneyUpdated={setJourney}
            readOnly={readOnly}
          />
        )}

        {/* Doc checklist builder — tab-gated */}
        {activeTab === 'doc_checklist' && (
          <DocChecklistBuilder
            prospectId={prospect.id}
            proposedDocItems={stage.proposed_doc_items || []}
            onJourneyUpdated={setJourney}
            readOnly={readOnly}
          />
        )}

        {/* Document collection workspace (Stage 3) — full width, no tabs */}
        {hasDocTable && (
          <DocCollectionWorkspace
            prospectId={prospect.id}
            documentItems={stage.document_items || []}
            readOnly={readOnly}
            onJourneyUpdated={setJourney}
          />
        )}

      </div>
    );
  }

  function renderMilestone(stage, m, readOnly) {
    const noteKey  = stage.stage_key + '__' + m.milestone_key;
    const isExpanded = !!expandedNotes[noteKey];

    return (
      <div key={m.milestone_key} className="pw__milestone-wrap">
        <div className="pw__milestone">
          <input
            type="checkbox"
            className="pw__milestone-check"
            checked={!!m.checked}
            disabled={readOnly || m.auto_tickable || m.repeatable}
            onChange={() => {
              if (!readOnly && !m.auto_tickable && !m.repeatable) {
                handleTickMilestone(stage.stage_key, m.milestone_key, !m.checked);
              }
            }}
          />
          <span className={`pw__milestone-label ${m.checked ? 'pw__milestone-label--done' : ''}`}>
            {m.label}
          </span>
          {m.auto_tickable && <span className="pw__auto-pill">Auto</span>}
          {m.repeatable && <span className="pw__repeatable-pill">Repeatable</span>}
          {!readOnly && m.required && !m.checked && <span className="pw__required-star">*</span>}
          {m.checked && m.checked_at && (
            <span className="pw__checked-at">{formatDate(m.checked_at)}</span>
          )}
          {!readOnly && !m.auto_tickable && !m.repeatable && (
            <button className="pw__notes-toggle" onClick={() => toggleNotes(noteKey)} title="Notes">
              📝
            </button>
          )}
        </div>

        {/* Milestone notes — editable when active, read-only when complete */}
        {(isExpanded || (readOnly && m.notes)) && (
          readOnly ? (
            m.notes && <div className="pw__milestone-notes pw__milestone-notes--readonly">{m.notes}</div>
          ) : (
            <textarea
              className="pw__milestone-notes"
              placeholder="Add milestone notes..."
              defaultValue={m.notes || ''}
              onBlur={e => handleSaveMilestoneNotes(stage.stage_key, m.milestone_key, !!m.checked, e.target.value)}
              rows={2}
            />
          )
        )}

        {m.repeatable && (
          <div className="pw__revision-section">
            {(m.revisions || []).map((r, i) => (
              <div key={i} className="pw__revision-entry">
                <span className="pw__revision-time">{formatDate(r.logged_at)}</span>
                <span className="pw__revision-notes">{r.notes}</span>
              </div>
            ))}
            {!readOnly && (
              <button className="pw__add-revision-btn" onClick={() => handleAddRevision(stage.stage_key, m.milestone_key)}>
                + Log Revision
              </button>
            )}
          </div>
        )}

        {FILE_MILESTONES.has(m.milestone_key) && (
          <MilestoneFilePanel
            prospectId={prospect.id}
            stageKey={stage.stage_key}
            milestoneKey={m.milestone_key}
            files={m.files || []}
            onJourneyUpdated={setJourney}
            readOnly={readOnly}
            showProfileDownload={PROFILE_SNAPSHOT_MILESTONES.has(m.milestone_key)}
            clientId={prospect.id}
          />
        )}
      </div>
    );
  }

}
